"use client";

import * as React from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { cn } from "@/lib/utils";

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end" | "center";
  side?: "bottom" | "top" | "left" | "right";
}

const DropdownContext = React.createContext<{
  open: boolean;
  setOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  opRef: React.RefObject<OverlayPanel | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
} | null>(null);

const DropdownMenu = ({ children, className, align, side = "bottom" }: DropdownMenuProps) => {
  const [open, setOpen] = React.useState(false);
  const opRef = React.useRef<OverlayPanel>(null);
  const triggerRef = React.useRef<HTMLElement>(null);

  return (
    <DropdownContext.Provider value={{ open, setOpen, opRef, triggerRef }}>
      <div className={cn("relative", className)}>{children}</div>
    </DropdownContext.Provider>
  );
};

interface DropdownMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
  ({ className, children, asChild, ...props }, ref) => {
    const ctx = React.useContext(DropdownContext);
    if (!ctx) throw new Error("DropdownMenuTrigger must be used within DropdownMenu");

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      props.onClick?.(e);
      if (ctx.opRef.current && ctx.triggerRef.current) {
        ctx.opRef.current.toggle(e);
      }
      ctx.setOpen((v) => !v);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        ref: (node: HTMLElement) => {
          ctx.triggerRef.current = node;
          if (typeof ref === "function") ref(node as HTMLButtonElement);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node as HTMLButtonElement;
        },
        onClick: handleClick,
      } as any);
    }

    return (
      <button
        ref={(node) => {
          if (node) ctx.triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        }}
        type="button"
        className={cn("p-button p-button-text", className)}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement> & { align?: "start" | "end" | "center" }
>(({ className, children, align }, ref) => {
  const ctx = React.useContext(DropdownContext);
  if (!ctx) throw new Error("DropdownMenuContent must be used within DropdownMenu");

  // Função recursiva para processar filhos, incluindo fragments
  const processChildren = (children: React.ReactNode): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let keyCounter = 0;
    
    const process = (children: React.ReactNode): void => {
      React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) {
          if (child !== null && child !== undefined && child !== false) {
            result.push(child);
          }
          return;
        }

        // Se for um Fragment, processa seus filhos recursivamente
        if (child.type === React.Fragment) {
          const fragmentProps = child.props as { children?: React.ReactNode };
          process(fragmentProps.children);
          return;
        }

        const uniqueKey = `dropdown-item-${keyCounter++}`;

        if (child.type === DropdownMenuItem) {
          const props = child.props as React.LiHTMLAttributes<HTMLLIElement> & { children?: React.ReactNode };
          result.push(
            <button
              key={uniqueKey}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-150 text-sm w-full bg-white hover:bg-gray-100 text-gray-900 text-left",
                props.className
              )}
              onClick={(event) => {
                props.onClick?.(event as any);
                ctx.setOpen(false);
                ctx.opRef.current?.hide();
              }}
              type="button"
            >
              {props.children}
            </button>
          );
          return;
        }

        if (child.type === DropdownMenuLabel) {
          const props = child.props as React.LiHTMLAttributes<HTMLLIElement> & { children?: React.ReactNode };
          result.push(
            <li key={uniqueKey} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
              {props.children}
            </li>
          );
          return;
        }

        if (child.type === DropdownMenuSeparator) {
          result.push(<li key={uniqueKey} className="mx-1 my-1 h-px bg-gray-200" />);
          return;
        }

        result.push(child);
      });
    };

    process(children);
    return result;
  };

  return (
    <OverlayPanel ref={ctx.opRef} className={cn("p-overlaypanel-custom", className)}>
      <ul className="list-none p-0 m-0 flex flex-col gap-0.5">
        {processChildren(children)}
      </ul>
    </OverlayPanel>
  );
});
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef<
  HTMLLIElement,
  React.LiHTMLAttributes<HTMLLIElement>
>(({ className, children, onClick, ...props }, ref) => {
  // DropdownMenuItem é processado pelo DropdownMenuContent
  return null;
});
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuLabel = React.forwardRef<
  HTMLLIElement,
  React.LiHTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => {
  // DropdownMenuLabel é processado pelo DropdownMenuContent
  return null;
});
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = React.forwardRef<
  HTMLLIElement,
  React.LiHTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => {
  // DropdownMenuSeparator é processado pelo DropdownMenuContent
  return null;
});
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuGroup = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuPortal = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuSub = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuRadioGroup = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuSubTrigger = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const DropdownMenuSubContent = ({ children }: { children: React.ReactNode }) => <>{children}</>;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuRadioGroup,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
